// KoachContentsContentsEdit: create/update koach content with required base video and optional per-language videos.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createContent, updateContent, fetchContentDetail, deleteContent, ICreateContentParams, IUpdateContentParams } from 'api/koach/v2/content.api';
import { getCurrentLanguageCode } from 'helpers/String.helper';
import { uploadVideoWithThumbnail } from 'api/common/Data.api';
import VideoPlayer from 'commons/video/VideoPlayer';
import VideoUploadFlowPopup, { VideoUploadFlowPhase } from "commons/popup/VideoUploadFlowPopup.component";

const KoachContentsContentsEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const videoRef = useRef<HTMLVideoElement>(null);

  type LangTab = 'ko' | 'en' | 'ja' | 'th' | 'vi' | 'id';
  const LANG_TABS: Array<{ code: LangTab; label: string }> = [
    { code: 'ko', label: '국문' },
    { code: 'en', label: '영문' },
    { code: 'ja', label: '일문' },
    { code: 'th', label: '태국어' },
    { code: 'vi', label: '베트남어' },
    { code: 'id', label: '인도네시아어' },
  ];

  type VideoByLang = {
    videoId: string;
    file: File | null;
    width: number;
    height: number;
    url: string | null;
    existingVideo: any | null;
    isUploading: boolean;
    progress: number;
  };

  const [formData, setFormData] = useState({
    title: '',
    desc: '',
  });

  const [loading, setLoading] = useState(false);
  const [isLoadingContent, setIsLoadingContent] = useState(isEditMode);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [activeLang, setActiveLang] = useState<LangTab>((getCurrentLanguageCode() as LangTab) || 'ko');
  const [videoByLang, setVideoByLang] = useState<Record<LangTab, VideoByLang>>({
    ko: { videoId: '', file: null, width: 0, height: 0, url: null, existingVideo: null, isUploading: false, progress: 0 },
    en: { videoId: '', file: null, width: 0, height: 0, url: null, existingVideo: null, isUploading: false, progress: 0 },
    ja: { videoId: '', file: null, width: 0, height: 0, url: null, existingVideo: null, isUploading: false, progress: 0 },
    th: { videoId: '', file: null, width: 0, height: 0, url: null, existingVideo: null, isUploading: false, progress: 0 },
    vi: { videoId: '', file: null, width: 0, height: 0, url: null, existingVideo: null, isUploading: false, progress: 0 },
    id: { videoId: '', file: null, width: 0, height: 0, url: null, existingVideo: null, isUploading: false, progress: 0 },
  });
  const objectUrlByLangRef = useRef<Record<LangTab, string | null>>({
    ko: null,
    en: null,
    ja: null,
    th: null,
    vi: null,
    id: null,
  });
  const [uploadPopup, setUploadPopup] = useState({
    isVisible: false,
    phase: "uploading" as VideoUploadFlowPhase,
    currentStep: "",
    videoUploadProgress: 0,
    errorMessage: "",
    isSaving: false,
  });

  const closeUploadPopup = () => {
    setUploadPopup(prev => ({ ...prev, isVisible: false, errorMessage: "", isSaving: false }));
  };

  const loadVideoMeta = (file: File): Promise<{ width: number; height: number; duration: number }> => {
    return new Promise((resolve, reject) => {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => resolve({ width: v.videoWidth, height: v.videoHeight, duration: v.duration });
      v.onerror = () => reject(new Error('invalid video'));
      v.src = URL.createObjectURL(file);
    });
  };

  const updateLangVideo = (lang: LangTab, patch: Partial<VideoByLang>) => {
    setVideoByLang(prev => {
      const prevRow = prev[lang];
      const nextRow = { ...prevRow, ...patch };
      return { ...prev, [lang]: nextRow };
    });
  };

  const clearLangVideo = (lang: LangTab) => {
    setVideoByLang(prev => {
      const target = prev[lang];
      const refUrl = objectUrlByLangRef.current[lang];
      if (refUrl) URL.revokeObjectURL(refUrl);
      objectUrlByLangRef.current[lang] = null;
      return {
        ...prev,
        [lang]: { videoId: '', file: null, width: 0, height: 0, url: null, existingVideo: null, isUploading: false, progress: 0 },
      };
    });
  };

  useEffect(() => {
    const loadData = async () => {
      // 수정 모드인 경우 기존 데이터 로드
      if (isEditMode && id) {
        try {
          setIsLoadingContent(true);
          const response = await fetchContentDetail(Number(id));
          const content = response.content;

          // 기본 제목 및 설명 사용 (또는 번역에서 가져오기)
          let title = content.title || '';
          let desc = content.desc || '';
          
          // 번역이 있고 기본값이 없으면 번역에서 가져오기
          if (content.multiTexts && content.multiTexts.length > 0) {
            const currentLang = getCurrentLanguageCode();
            const currentMultiText = content.multiTexts.find(mt => mt.languageCode === currentLang);
            if (!title && currentMultiText?.title) title = currentMultiText.title;
            if (!desc && currentMultiText?.desc) desc = currentMultiText.desc;
          }

          setFormData({
            title,
            desc,
          });

          // 언어별 비디오 (ko 포함)
          if (Array.isArray(content.videos)) {
            content.videos.forEach((v: any) => {
              const code = v.languageCode as LangTab;
              if (!code || !LANG_TABS.find(t => t.code === code)) return;
              updateLangVideo(code, { videoId: v.videoId || '', existingVideo: v.video || null });
            });
          } else if (content.video && (content.video as any).id) {
            // fallback: 서버가 ko video만 내려주는 경우
            updateLangVideo('ko', { videoId: (content.video as any).id, existingVideo: content.video });
          }
        } catch (error) {
          console.error('컨텐츠 조회 실패:', error);
          alert('컨텐츠를 불러오는데 실패했습니다.');
          navigate('/koach/coaching/contents');
        } finally {
          setIsLoadingContent(false);
        }
      }
    };

    loadData();
  }, [isEditMode, id, navigate]);

  const handleTitleChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      title: value
    }));
    if (errors.title) {
      setErrors(prev => ({ ...prev, title: '' }));
    }
  };

  const handleDescChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      desc: value
    }));
  };

  // objectURL은 언마운트 시에만 전체 정리 (탭 이동/상태 변경 때 revoke하면 재생이 깨짐)
  useEffect(() => {
    return () => {
      (Object.keys(objectUrlByLangRef.current) as LangTab[]).forEach((lang) => {
        const url = objectUrlByLangRef.current[lang];
        if (url) URL.revokeObjectURL(url);
        objectUrlByLangRef.current[lang] = null;
      });
    };
  }, []);

  const onLangVideoFileChange = (lang: LangTab, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      alert('영상 파일만 업로드할 수 있습니다.');
      return;
    }
    loadVideoMeta(file)
      .then(meta => {
        if (meta.duration > 900) {
          alert('영상은 최대 15분까지만 업로드 가능합니다.');
          return;
        }
        const url = URL.createObjectURL(file);
        // 이전 url 정리 (언어별로만)
        const prevUrl = objectUrlByLangRef.current[lang];
        if (prevUrl) URL.revokeObjectURL(prevUrl);
        objectUrlByLangRef.current[lang] = url;
        updateLangVideo(lang, { file, url, width: meta.width, height: meta.height, progress: 0, existingVideo: null, videoId: '' });
        if (lang === 'ko' && errors.video) setErrors(prev => ({ ...prev, video: '' }));
      })
      .catch(() => alert('영상 파일만 업로드할 수 있습니다.'));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    // 제목 검증
    if (!formData.title.trim()) {
      newErrors.title = '제목을 입력해주세요.';
    }

    // 최소 1개 영상은 필수 (어떤 언어든 가능)
    const hasAnyVideo = (Object.keys(videoByLang) as LangTab[]).some((lang) => {
      const v = videoByLang[lang];
      return !!v.file || !!v.videoId || !!v.existingVideo;
    });
    if (!hasAnyVideo) {
      newErrors.video = '영상 탭에서 동영상 1개 이상을 업로드해주세요.';
      // 사용자가 현재 언어 탭을 먼저 보도록
      setActiveLang(((getCurrentLanguageCode() as LangTab) || 'ko'));
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setUploadPopup({
        isVisible: true,
        phase: "uploading",
        currentStep: "영상 업로드 준비 중...",
        videoUploadProgress: 0,
        errorMessage: "",
        isSaving: false,
      });
      // 탭별 업로드 (등록된 언어만 전송)
      const uploadedVideoList: Array<{ languageCode: string; videoId: string }> = [];
      const allTabs: LangTab[] = ['ko', 'en', 'ja', 'th', 'vi', 'id'];

      for (const lang of allTabs) {
        let vid = videoByLang[lang].videoId || (videoByLang[lang].existingVideo?.id as string) || '';

        if (videoByLang[lang].file) {
          updateLangVideo(lang, { isUploading: true, progress: 0 });
          setUploadPopup(prev => ({
            ...prev,
            currentStep: `${LANG_TABS.find(t => t.code === lang)?.label} 영상 업로드 중...`,
            videoUploadProgress: 0,
          }));
          try {
            const resp = await uploadVideoWithThumbnail(
              videoByLang[lang].file as File,
              videoByLang[lang].width,
              videoByLang[lang].height,
              false,
              undefined,
              true,
              (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                updateLangVideo(lang, { progress: percentCompleted });
                setUploadPopup(prev => ({ ...prev, videoUploadProgress: percentCompleted }));
              }
            );

            if (!resp?.id) {
              alert(`${LANG_TABS.find(t => t.code === lang)?.label} 동영상 업로드에 실패했습니다. 다시 시도해주세요.`);
              setUploadPopup(prev => ({
                ...prev,
                errorMessage: `${LANG_TABS.find(t => t.code === lang)?.label} 동영상 업로드에 실패했습니다.`,
              }));
              return;
            }
            vid = resp.id;
          } finally {
            updateLangVideo(lang, { isUploading: false });
          }
        }

        if (vid) {
          uploadedVideoList.push({ languageCode: lang, videoId: vid });
        }
      }

      if (uploadedVideoList.length === 0) {
        alert('동영상을 1개 이상 업로드해주세요.');
        setUploadPopup(prev => ({ ...prev, errorMessage: "동영상을 1개 이상 업로드해주세요." }));
        return;
      }

      setUploadPopup(prev => ({ ...prev, currentStep: "저장 중...", isSaving: true }));
      if (isEditMode && id) {
        // 수정 - 기본 제목/설명을 contents 테이블에 저장
        const params: IUpdateContentParams = {
          title: formData.title.trim() || undefined,
          desc: formData.desc.trim() || undefined,
          videoList: uploadedVideoList
        };
        await updateContent(Number(id), params);
        alert('컨텐츠가 수정되었습니다.');
      } else {
        // 생성 - 기본 제목/설명을 contents 테이블에 저장
        const params: ICreateContentParams = {
          title: formData.title.trim() || undefined,
          desc: formData.desc.trim() || undefined,
          videoList: uploadedVideoList
        };
        await createContent(params);
        alert('컨텐츠가 생성되었습니다.');
      }

      navigate('/koach/coaching/contents');
    } catch (error: any) {
      console.error('컨텐츠 저장 실패:', error);
      alert(error.message || '컨텐츠 저장에 실패했습니다.');
      setUploadPopup(prev => ({ ...prev, errorMessage: error?.message || '컨텐츠 저장에 실패했습니다.', isSaving: false }));
    } finally {
      setLoading(false);
      setUploadPopup(prev => ({ ...prev, isVisible: false, isSaving: false }));
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    if (!window.confirm('정말 이 컨텐츠를 삭제하시겠습니까?')) {
      return;
    }

    try {
      setLoading(true);
      await deleteContent(Number(id));
      alert('컨텐츠가 삭제되었습니다.');
      navigate('/koach/coaching/contents');
    } catch (error: any) {
      console.error('컨텐츠 삭제 실패:', error);
      alert(error.message || '컨텐츠 삭제에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  if (isLoadingContent) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 pb-4 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-center items-center h-64">
            <div className="text-gray-500">로딩 중...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main className="bg-gray-50 min-h-screen pb-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate(-1)}
            className="mr-2 p-2 rounded-full hover:bg-gray-200"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h2 className="text-3xl font-bold text-gray-900">
            {isEditMode ? '콘텐츠 수정' : '새 콘텐츠 작성'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
            {/* 제목 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                제목 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="제목을 입력하세요"
              />
              {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
            </div>

            {/* 영상 섹션 (탭) */}
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="border-b bg-gray-50 px-4 py-3">
                <h3 className="text-base font-semibold text-gray-900">영상</h3>
                <p className="text-sm text-gray-600 mt-1">탭을 선택하면 해당 언어의 영상 업로드/미리보기가 바뀝니다.</p>
              </div>

              <div className="px-4 pt-4">
                <div className="flex gap-2 flex-wrap">
                  {LANG_TABS.map(t => (
                    <button
                      key={t.code}
                      type="button"
                      onClick={() => setActiveLang(t.code)}
                      className={`px-3 py-2 rounded-full text-sm border transition-colors ${
                        activeLang === t.code
                          ? 'bg-[#CF3688] text-white border-[#CF3688]'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {t.label}
                      {(videoByLang[t.code].file || videoByLang[t.code].videoId || videoByLang[t.code].existingVideo) && (
                        <span className={`ml-2 inline-block w-2 h-2 rounded-full ${activeLang === t.code ? 'bg-white' : 'bg-emerald-500'}`} />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="px-4 pb-4 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-medium text-gray-900">
                    {LANG_TABS.find(t => t.code === activeLang)?.label} 영상
                  </div>
                  {(videoByLang[activeLang].file || videoByLang[activeLang].videoId || videoByLang[activeLang].existingVideo) && (
                    <button
                      type="button"
                      onClick={() => clearLangVideo(activeLang)}
                      className="text-sm text-red-600 hover:text-red-800"
                    >
                      삭제
                    </button>
                  )}
                </div>

                {!videoByLang[activeLang].file && !videoByLang[activeLang].videoId && !videoByLang[activeLang].existingVideo && (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                    <input
                      type="file"
                      accept="video/*"
                      onChange={(e) => onLangVideoFileChange(activeLang, e)}
                      className="hidden"
                      id={`videoInput_${activeLang}`}
                    />
                    <label htmlFor={`videoInput_${activeLang}`} className="cursor-pointer flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <span className="text-sm text-gray-600">비디오 파일을 선택하세요</span>
                      <span className="text-xs text-gray-400 mt-1">최대 15분까지 업로드 가능</span>
                    </label>
                  </div>
                )}

                {videoByLang[activeLang].file && videoByLang[activeLang].url && (
                  <div className="relative">
                    <video ref={videoRef} src={videoByLang[activeLang].url as string} controls className="w-full rounded-lg" style={{ maxHeight: '420px' }} />
                  </div>
                )}

                {videoByLang[activeLang].existingVideo && !videoByLang[activeLang].file && (
                  <div className="relative">
                    <VideoPlayer video={videoByLang[activeLang].existingVideo} className="w-full rounded-lg" enableVideoLog={false} />
                  </div>
                )}

                {videoByLang[activeLang].isUploading && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">비디오 업로드 중...</span>
                      <span className="text-sm text-gray-600">{videoByLang[activeLang].progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-[#CF3688] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${videoByLang[activeLang].progress}%` }}
                      />
                    </div>
                  </div>
                )}

                {errors.video && <p className="mt-2 text-sm text-red-500">{errors.video}</p>}
              </div>
            </div>

            {/* 설명 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">설명</label>
              <textarea
                value={formData.desc}
                onChange={(e) => handleDescChange(e.target.value)}
                rows={4}
                className="w-full p-2 border border-gray-300 rounded-md"
                placeholder="설명을 입력하세요"
              />
            </div>

            {/* 버튼 */}
            <div className="flex justify-between items-center pt-4 border-t">
              <div>
                {isEditMode && id && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md disabled:opacity-50"
                    disabled={loading}
                  >
                    삭제
                  </button>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                  disabled={loading}
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-[#CF3688] hover:bg-[#B52E74] rounded-md disabled:opacity-50"
                  disabled={loading || Object.values(videoByLang).some(v => v.isUploading)}
                >
                  {loading ? '저장 중...' : '저장'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      <VideoUploadFlowPopup
        isVisible={uploadPopup.isVisible}
        phase={uploadPopup.phase}
        progress={uploadPopup.videoUploadProgress}
        currentStep={uploadPopup.currentStep}
        canCancel={false}
        errorMessage={uploadPopup.errorMessage}
        thumbnails={[]}
        selectedIndex={0}
        isSaving={uploadPopup.isSaving}
        labels={{
          close: "닫기",
          save: "저장",
          uploading: "잠시만 기다려주세요",
          thumbnailTitle: "썸네일 선택",
        }}
        onCancelUpload={closeUploadPopup}
        onClose={closeUploadPopup}
        onSave={() => {}}
        onSelectThumbnail={() => {}}
      />
    </main>
  );
};

export default KoachContentsContentsEdit;
